import React, { useRef } from 'react';

import { AskAiRule } from '@lumapps/ask-ai-rules/types';
import type { AskSearchBestAnswer } from '@lumapps/ask-ai/types';
import { margin, useClassnames } from '@lumapps/classnames';
import { ChatThread } from '@lumapps/lumx-chat/components/ChatThread';
import { Divider, FlexBox, Orientation } from '@lumapps/lumx/react';

import { useAskAiConversation } from '../../hooks/useAskAiConversation';
import { AskAiChatMessagesThread } from '../AskAiChatMessagesThread';
import { AskAiSuggestedQuestions } from '../AskAiSuggestedQuestions';
import { ResumeConversationButton } from '../ResumeConversationButton';
import { AskAiFollowUpQuestionsFooter } from './AskAiFollowUpQuestionsFooter';

import './index.scss';

export interface AskAiFollowUpInputProps {
    /** The initial traceId to use */
    initialTraceId: string;
    /** The current open status */
    isOpen?: boolean;
    /** callback when the open status should change */
    setIsOpen: (value: boolean) => void;
    /**
     * The suggested questions to initially display.
     * Once a follow up question has been asked, the new suggested questions will
     * take their place
     */
    initialSuggestedQuestions?: AskSearchBestAnswer['suggestedFollowUpQuestions'];
    /** Id of the current conversation used to keep context between successive answers */
    conversationId?: AskSearchBestAnswer['conversationId'];
    /**
     * Whether the result of the query issued from
     * the search bar's AI Agent suggestion is currently streaming
     */
    isInitialResultStreaming?: boolean;
    /** The initial selected agent id - selected by the user */
    initialSelectedAgent?: AskAiRule;
}

const CLASSNAME = 'ask-ai-follow-up-questions';

/**
 * Section to ask follow up questions to Ask Ai.
 */
export const AskAiFollowUpQuestions: React.FC<AskAiFollowUpInputProps> = ({
    isOpen,
    setIsOpen,
    initialTraceId,
    initialSuggestedQuestions = [],
    conversationId = '',
    isInitialResultStreaming,
    initialSelectedAgent,
}) => {
    const { element } = useClassnames(CLASSNAME);

    const threadRef = useRef<HTMLDivElement>(null);

    const { messages, onSubmit, refetch, isStreaming, isLoading, stopStream, bestAnswer, handleChangeSelectedAgent } =
        useAskAiConversation({
            initialTraceId,
            conversationId,
            initialSelectedAgent,
        });

    const isStreamingOrLoading = isStreaming || isLoading;
    const followUpQuestionsLength = messages.filter((message) => message.role === 'user').length;
    const inputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    /**
     * Update selected agent and remove the specific assistant message
     */
    const handleAgentChange = (rule: AskAiRule, messageId: string) => {
        handleChangeSelectedAgent(messageId, rule);
    };

    /**
     * Open the conversation
     */
    const handleOpen = () => {
        setIsOpen(true);

        /**
         * If there is only one question in the conversational,
         * we scroll onto the whole thread instead of the message group
         * in order to also have the thread header in view.
         */
        if (followUpQuestionsLength <= 1) {
            threadRef.current?.scrollIntoView({
                block: 'start',
                behavior: 'smooth',
            });
        }
    };

    const handleSubmit = (value?: string) => {
        if (value?.trim()) {
            onSubmit(value);
            handleOpen();
        }
    };

    /**
     * Close the conversation and return to the top of the page
     */
    const closeAndScroll = () => {
        window.scrollTo({ top: 0, behavior: 'instant' });
        setIsOpen(false);
    };

    /**
     * Action called when a suggested question is selected.
     * Submit the question and restore focus on the input.
     */
    const handleSelectSuggestedQuestion = (question: string) => {
        handleSubmit(question);
        inputRef.current?.focus();
    };

    /**
     * When the follow-up section opens, prioritize focusing the stop button
     * if a stream is active or loading. Otherwise, focus the input field.
     */
    React.useEffect(() => {
        if (isOpen) {
            if (isStreamingOrLoading && buttonRef?.current) {
                buttonRef?.current?.focus();
            } else if (inputRef?.current) {
                inputRef?.current?.focus();
            }
        }
    }, [isOpen, isStreamingOrLoading]);

    return (
        <FlexBox ref={threadRef} orientation={Orientation.vertical}>
            {isOpen && (
                <FlexBox orientation={Orientation.vertical} gap="big">
                    <Divider />
                    <ChatThread className={element('thread')}>
                        <AskAiChatMessagesThread
                            messages={messages}
                            refetch={refetch}
                            isStreaming={isStreaming}
                            bestAnswer={bestAnswer}
                            onSelectSuggestedQuestion={handleSelectSuggestedQuestion}
                            shouldDisplayAgentName
                            handleAgentChange={handleAgentChange}
                        />
                    </ChatThread>
                </FlexBox>
            )}
            {!isOpen && followUpQuestionsLength > 0 ? (
                <ResumeConversationButton
                    followUpQuestionsLength={followUpQuestionsLength}
                    onClick={handleOpen}
                    className={element('restore-follow-up-button')}
                />
            ) : (
                <>
                    {!isStreaming && initialSuggestedQuestions && followUpQuestionsLength === 0 && (
                        <AskAiSuggestedQuestions
                            className={element('suggestions', [margin('left', 'big')])}
                            suggestionQuestions={initialSuggestedQuestions}
                            onSelect={handleSelectSuggestedQuestion}
                        />
                    )}
                    <AskAiFollowUpQuestionsFooter
                        isOpen={isOpen}
                        inputRef={inputRef}
                        buttonRef={buttonRef}
                        onClose={closeAndScroll}
                        isStreamingOrLoading={isStreamingOrLoading}
                        isInitialResultStreaming={isInitialResultStreaming}
                        onSubmit={handleSubmit}
                        onStopStream={stopStream}
                    />
                </>
            )}
        </FlexBox>
    );
};
